import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  X,
  Users,
  User,
  CalendarDots,
  Repeat,
  Check,
  Clock,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { useEndpoints } from '../../../../hooks/useEndpoints';
import {
  AssignFieldWorkoutBody,
  FieldPeriodicity,
  FieldPeriodicityEndType,
} from '../../../../models/field-programming';
import { ITeam } from '../../../../models/Team';
import { IUser2 } from '../../../../models/user copy';
import { Paginated } from '../../../../typings/Paginated';
import { formatFullName } from '../../../../utils/functions';
import styles from '../FieldWorkoutBuilderModal.module.scss';

interface Props {
  data: AssignFieldWorkoutBody;
  onChange: (d: Partial<AssignFieldWorkoutBody>) => void;
}

const PERIODICITY_LABELS: Record<FieldPeriodicity, string> = {
  none: 'Nessuna',
  weekly: 'Settimanale',
  biweekly: 'Bisettimanale',
  monthly: 'Mensile',
};

const PERIODICITY_END_LABELS: Record<FieldPeriodicityEndType, string> = {
  after_n: 'Dopo N volte',
  on_date: 'In data',
};

const AssignStep: FC<Props> = ({ data, onChange }) => {
  const { getTeams, getUsers } = useEndpoints();
  const [teamSearch, setTeamSearch] = useState('');
  const [athleteSearch, setAthleteSearch] = useState('');

  const teamsQ = useQuery({
    queryKey: ['teams-for-field-assign'],
    queryFn: async () => {
      const { data: res } = await getTeams({ limit: 100 });
      const arr = (res as Paginated<ITeam> | undefined)?.docs ?? (res as unknown as ITeam[] | undefined) ?? [];
      return arr as ITeam[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const athletesQ = useQuery({
    queryKey: ['athletes-for-field-assign'],
    queryFn: async () => {
      const { data: res } = await getUsers({ limit: 100, type: 'athlete' });
      const arr = (res as Paginated<IUser2> | undefined)?.docs ?? (res as unknown as IUser2[] | undefined) ?? [];
      return arr as IUser2[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const teams = useMemo(() => teamsQ.data ?? [], [teamsQ.data]);
  const athletes = useMemo(() => athletesQ.data ?? [], [athletesQ.data]);

  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    const lower = teamSearch.toLowerCase();
    return teams.filter((t) => t.name.toLowerCase().includes(lower));
  }, [teams, teamSearch]);

  const filteredAthletes = useMemo(() => {
    if (!athleteSearch.trim()) return athletes;
    const lower = athleteSearch.toLowerCase();
    return athletes.filter((a) =>
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(lower),
    );
  }, [athletes, athleteSearch]);

  const mode: 'team' | 'athletes' =
    data.athleteIds && data.athleteIds.length > 0 ? 'athletes' : 'team';

  const setMode = (m: 'team' | 'athletes') => {
    if (m === 'team') {
      onChange({ athleteIds: undefined });
    } else {
      onChange({ teamId: undefined, athleteIds: [] });
    }
  };

  const toggleAthlete = (id: string) => {
    const current = data.athleteIds ?? [];
    onChange({
      athleteIds: current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id],
    });
  };

  const validDates = data.scheduledDates.filter(Boolean);

  const periodicity = data.periodicity ?? 'none';
  const periodicityEnd = data.periodicityEnd;

  return (
    <div className={styles.assignBody}>
      {/* Destinatario */}
      <div className={styles.assignSection}>
        <p className={styles.sectionLabel}>Destinatario</p>

        <div className={styles.radioGroup}>
          <label
            className={`${styles.radioOption} ${mode === 'team' ? styles.radioOptionActive : ''}`}
          >
            <input
              type="radio"
              name="assigneeMode"
              checked={mode === 'team'}
              onChange={() => setMode('team')}
              className={styles.hiddenInput}
            />
            <div
              className={`${styles.radioCircle} ${mode === 'team' ? styles.radioCircleChecked : ''}`}
            >
              {mode === 'team' && <div className={styles.radioInner} />}
            </div>
            <div className={styles.radioLabel}>
              <span className={styles.radioTitle}>
                <Users size={14} weight="bold" />
                Team
              </span>
              <span className={styles.radioDesc}>Assegna a un team intero</span>
            </div>
          </label>

          <label
            className={`${styles.radioOption} ${mode === 'athletes' ? styles.radioOptionActive : ''}`}
          >
            <input
              type="radio"
              name="assigneeMode"
              checked={mode === 'athletes'}
              onChange={() => setMode('athletes')}
              className={styles.hiddenInput}
            />
            <div
              className={`${styles.radioCircle} ${mode === 'athletes' ? styles.radioCircleChecked : ''}`}
            >
              {mode === 'athletes' && <div className={styles.radioInner} />}
            </div>
            <div className={styles.radioLabel}>
              <span className={styles.radioTitle}>
                <User size={14} weight="bold" />
                Atleti singoli
              </span>
              <span className={styles.radioDesc}>Seleziona uno o più atleti</span>
            </div>
          </label>
        </div>

        {mode === 'team' && (
          <div className={styles.assigneePanel}>
            <div className={styles.searchInline}>
              <MagnifyingGlass size={14} className={styles.searchInlineIcon} weight="bold" />
              <input
                className={styles.searchInlineInput}
                placeholder="Cerca team..."
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
              />
            </div>
            <div className={styles.listBox}>
              {teamsQ.isLoading ? (
                <p className={styles.listEmpty}>Caricamento...</p>
              ) : filteredTeams.length === 0 ? (
                <p className={styles.listEmpty}>Nessun team trovato</p>
              ) : (
                filteredTeams.map((team) => {
                  const selected = data.teamId === team._id;
                  return (
                    <label
                      key={team._id}
                      className={`${styles.checkRow} ${selected ? styles.checkRowActive : ''}`}
                    >
                      <div
                        className={`${styles.checkSquare} ${selected ? styles.checkSquareChecked : ''}`}
                      >
                        {selected && <Check size={12} weight="bold" />}
                      </div>
                      <div className={styles.checkInfo}>
                        <span className={styles.checkLabel}>{team.name}</span>
                      </div>
                      <input
                        type="radio"
                        name="teamPick"
                        checked={selected}
                        onChange={() => onChange({ teamId: team._id })}
                        className={styles.hiddenInput}
                      />
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}

        {mode === 'athletes' && (
          <div className={styles.assigneePanel}>
            <div className={styles.searchInline}>
              <MagnifyingGlass size={14} className={styles.searchInlineIcon} weight="bold" />
              <input
                className={styles.searchInlineInput}
                placeholder="Cerca atleta..."
                value={athleteSearch}
                onChange={(e) => setAthleteSearch(e.target.value)}
              />
            </div>
            <div className={styles.listBox}>
              {athletesQ.isLoading ? (
                <p className={styles.listEmpty}>Caricamento...</p>
              ) : filteredAthletes.length === 0 ? (
                <p className={styles.listEmpty}>Nessun atleta trovato</p>
              ) : (
                filteredAthletes.map((a) => {
                  const selected = data.athleteIds?.includes(a._id) ?? false;
                  return (
                    <label
                      key={a._id}
                      className={`${styles.checkRow} ${selected ? styles.checkRowActive : ''}`}
                    >
                      <div
                        className={`${styles.checkSquare} ${selected ? styles.checkSquareChecked : ''}`}
                      >
                        {selected && <Check size={12} weight="bold" />}
                      </div>
                      <div className={styles.checkInfo}>
                        <span className={styles.checkLabel}>
                          {formatFullName(a.firstName, a.lastName)}
                        </span>
                        <span className={styles.checkSub}>{a.email}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleAthlete(a._id)}
                        className={styles.hiddenInput}
                      />
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Date */}
      <div className={styles.assignSection}>
        <p className={styles.sectionLabel}>
          <CalendarDots size={14} weight="bold" style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Date sessione
        </p>
        <div className={styles.inlineCalendarWrapper}>
          <div className={styles.dateAddRow}>
            <input
              type="date"
              className={styles.input}
              style={{ maxWidth: 220 }}
              onChange={(e) => {
                const iso = e.target.value;
                if (!iso) return;
                if (!validDates.includes(iso)) {
                  onChange({ scheduledDates: [...validDates, iso] });
                }
                e.target.value = '';
              }}
            />
            <span className={styles.dateAddHint}>Aggiungi una o più date alla scheda</span>
          </div>

          {validDates.length > 0 ? (
            <div className={styles.selectedDatesRow}>
              {[...validDates].sort().map((d) => (
                <span key={d} className={styles.selectedDateChip}>
                  {dayjs(d).format('D MMM YYYY')}
                  <button
                    type="button"
                    className={styles.selectedDateChipRemove}
                    onClick={() =>
                      onChange({
                        scheduledDates: validDates.filter((x) => x !== d),
                      })
                    }
                    title="Rimuovi"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className={styles.noDateHint}>Aggiungi almeno una data per la sessione</p>
          )}
        </div>
      </div>

      {/* Orario */}
      <div className={styles.assignSection}>
        <p className={styles.sectionLabel}>
          <Clock size={14} weight="bold" style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Orario di inizio (opzionale)
        </p>
        <div className={styles.timeRow}>
          <input
            type="time"
            className={styles.input}
            style={{ maxWidth: 180 }}
            value={data.startTime ?? ''}
            onChange={(e) => onChange({ startTime: e.target.value || undefined })}
          />
          <input
            type="number"
            min={0}
            className={styles.input}
            style={{ maxWidth: 180 }}
            placeholder="Durata (min)"
            value={data.durationMinutes ?? ''}
            onChange={(e) =>
              onChange({
                durationMinutes: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          />
        </div>
      </div>

      {/* Ricorrenza */}
      <div className={styles.assignSection}>
        <p className={styles.sectionLabel}>
          <Repeat size={14} weight="bold" style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Ricorrenza
        </p>
        <div className={styles.periodicityRow}>
          {(Object.keys(PERIODICITY_LABELS) as FieldPeriodicity[]).map((key) => {
            const isActive = periodicity === key;
            return (
              <button
                key={key}
                type="button"
                className={`${styles.periodicityChip} ${isActive ? styles.periodicityChipActive : ''}`}
                onClick={() => onChange({ periodicity: key })}
              >
                {PERIODICITY_LABELS[key]}
              </button>
            );
          })}
        </div>

        {periodicity !== 'none' && (
          <div className={styles.periodicityEndSection}>
            <p className={styles.subsectionLabel}>Termina</p>
            <div className={styles.periodicityEndRow}>
              {(Object.keys(PERIODICITY_END_LABELS) as FieldPeriodicityEndType[]).map(
                (endKey) => {
                  const isActive = periodicityEnd?.type === endKey;
                  return (
                    <button
                      key={endKey}
                      type="button"
                      className={`${styles.periodicityChip} ${isActive ? styles.periodicityChipActive : ''}`}
                      onClick={() =>
                        onChange({
                          periodicityEnd: {
                            type: endKey,
                            value: endKey === 'after_n' ? 4 : '',
                          },
                        })
                      }
                    >
                      {PERIODICITY_END_LABELS[endKey]}
                    </button>
                  );
                },
              )}
            </div>

            {periodicityEnd?.type === 'after_n' && (
              <div className={styles.periodicityEndInput}>
                <span className={styles.inlineLabel}>Dopo</span>
                <input
                  type="number"
                  min={1}
                  max={52}
                  className={styles.smallNumberInput}
                  value={typeof periodicityEnd.value === 'number' ? periodicityEnd.value : 4}
                  onChange={(e) =>
                    onChange({
                      periodicityEnd: {
                        type: 'after_n',
                        value: Math.max(1, parseInt(e.target.value) || 1),
                      },
                    })
                  }
                />
                <span className={styles.inlineLabel}>volte</span>
              </div>
            )}

            {periodicityEnd?.type === 'on_date' && (
              <div className={styles.periodicityEndInput}>
                <span className={styles.inlineLabel}>Il</span>
                <input
                  type="date"
                  className={styles.input}
                  style={{ maxWidth: 180 }}
                  value={typeof periodicityEnd.value === 'string' ? periodicityEnd.value : ''}
                  onChange={(e) =>
                    onChange({
                      periodicityEnd: { type: 'on_date', value: e.target.value },
                    })
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Note */}
      <div className={styles.assignSection}>
        <p className={styles.sectionLabel}>Note (opzionali)</p>
        <textarea
          className={styles.textarea}
          rows={2}
          placeholder="Eventuali note che lo staff vede in calendario..."
          value={data.notes ?? ''}
          onChange={(e) => onChange({ notes: e.target.value || undefined })}
        />
      </div>
    </div>
  );
};

export default AssignStep;
